import { HttpClient } from "@angular/common/http";
import { effect, inject, untracked } from "@angular/core";
import { WA_LOCAL_STORAGE, WA_LOCATION, WA_WINDOW } from "@ng-web-apis/common";
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { Centrifuge, Subscription } from "centrifuge";
import { computedPrevious } from "ngxtension/computed-previous";
import { catchError, filter, of, pipe, switchMap, tap, timeout } from "rxjs";
import { version } from "../../../../package.json";
import { SwUpdateService } from "../services/sw-update.service";
import { environment } from "../../../environments/environment";
import { LocalStorageJwtService } from "../services/token-storage/local-storage-jwt.service";

export function connectDisplayPresence(
  client: Centrifuge,
  onConnected: (subscription: Subscription) => void,
  onError: (context: unknown) => void,
): void {
  client.on("connected", () => {
    const channel = "displays";
    let subscription = client.getSubscription(channel);
    if (!subscription) {
      subscription = client.newSubscription(channel);
      subscription.subscribe();
    }
    onConnected(subscription);
  });
  client.on("error", onError);
  client.connect();
}

export interface AppState {
  token: string;
  version: string;
  readonly remoteVersion: string;
  isOnline: boolean;
  isFullScreen: boolean;
  lastRequestTime: number | undefined;
  // Se puede salir/entrar de pantalla completa
  isInteractive: boolean;
  manualRotation: "" | "90" | "270";
  centrifugoClient: Centrifuge | undefined;
  centrifugoSubscription: Subscription | undefined;
}

export const AppStore = signalStore(
  {
    providedIn: "root",
  },
  withState<AppState>({
    token: "",
    version,
    lastRequestTime: undefined,
    remoteVersion: version,
    isOnline: true,
    isFullScreen: false,
    isInteractive: true,
    manualRotation: "",
    centrifugoClient: undefined,
    centrifugoSubscription: undefined,
  }),
  withMethods(
    (
      store,
      http = inject(HttpClient),
      win = inject(WA_WINDOW),
      location = inject(WA_LOCATION),
      jwtStorage = inject(LocalStorageJwtService),
    ) => ({
      setIsInteractive: (isInteractive: boolean) => {
        patchState(store, { isInteractive });
      },
      setIsFullScreen: (isFullScreen: boolean) => {
        patchState(store, { isFullScreen });
      },
      setManualRotation: (manualRotation: "" | "90" | "270") => {
        patchState(store, {
          manualRotation,
        });
      },
      unlink: () => {
        jwtStorage.removeItem();

        const event = new CustomEvent("unlink-display");
        win.dispatchEvent(event);

        location.reload();
      },
      setToken: (token: string) => {
        patchState(store, { token });
      },
      setIsOnline: (isOnline: boolean) => {
        patchState(store, { isOnline });
      },
      fetchRemoteVersion: rxMethod<void>(
        pipe(
          filter(() => {
            const cacheMS = 1000 * 60 * 5;
            const lastRequest = store.lastRequestTime();
            return !lastRequest || Date.now() - lastRequest > cacheMS;
          }),
          switchMap(() =>
            http
              .get("/assets/VERSION.txt", {
                responseType: "text",
                params: { "ngsw-bypass": "true" },
                transferCache: false,
                headers: {
                  "Cache-Control": "no-store",
                },
              })
              .pipe(
                timeout({ first: 10_000 }),
                catchError(() => of(store.remoteVersion())),
                tap((version) => {
                  patchState(store, {
                    remoteVersion: version,
                    lastRequestTime: Date.now(),
                  });
                }),
              ),
          ),
        ),
      ),
      initCentrifugo: () => {
        if (!environment.centrifugoEnabled) {
          return;
        }

        const token = store.token();
        if (token) {
          // Si ya había un cliente conectado (p.ej. token refrescado) lo cerramos
          // para no dejar sockets ni listeners de "connected" acumulándose.
          const previousClient = store.centrifugoClient();
          if (previousClient) {
            try {
              previousClient.removeAllListeners();
              previousClient.disconnect();
            } catch {}
          }

          const client = new Centrifuge(environment.centrifugoUrl, {
            token: "",
            debug: true,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          connectDisplayPresence(
            client,
            (subscription) => {
              patchState(store, {
                centrifugoClient: client,
                centrifugoSubscription: subscription,
              });
            },
            (context) => console.log(context),
          );
        }
      },
    }),
  ),
  withComputed((store) => ({
    previusConnectionStatus: computedPrevious(store.isOnline),
  })),
  withHooks({
    onInit(
      store,
      storage = inject(LocalStorageJwtService),
      localStorage = inject(WA_LOCAL_STORAGE),
      win = inject(WA_WINDOW),
      updates = inject(SwUpdateService),
    ) {
      patchState(store, { isOnline: win.navigator.onLine });

      const safeGetLocalStorageItem = (key: string): string | null => {
        try {
          return localStorage?.getItem(key) ?? null;
        } catch {
          return null;
        }
      };

      const safeSetLocalStorageItem = (key: string, value: string): void => {
        try {
          localStorage?.setItem(key, value);
        } catch {
          // No rompemos la app si storage falla en este navegador.
        }
      };

      store.fetchRemoteVersion();

      const token = storage.getItemSync();

      if (token) {
        patchState(store, { token: token.toString() });
      }

      const rotation = safeGetLocalStorageItem("rotation");

      if (rotation) {
        patchState(store, { manualRotation: rotation as "" | "90" | "270" });
      }

      effect(() => {
        const t = store.token();

        if (!t) {
          return;
        }

        storage.setItem(t);

        // Emitimos el token a la ventana
        const event = new CustomEvent("display-token", {
          detail: t,
        });

        win.dispatchEvent(event);

        untracked(() => store.initCentrifugo());
      });

      effect(() => {
        safeSetLocalStorageItem("rotation", store.manualRotation());
      });

      // effect(() => {
      //   const isOnline = store.isOnline();
      //   const wasOnline = store.previusConnectionStatus();
      //   if (isOnline && isOnline !== wasOnline) {
      //     // Alos 2 segundo de volver la conexion recargamos la pagina
      //     setTimeout(() => location.reload(), 2000);
      //   }
      // });

      effect(() => {
        const currentVersion = store.version();
        console.log(`Player Version: ${currentVersion}`);
      });

      effect(() => {
        const remoteVersion = store.remoteVersion();
        const appVersion = store.version();
        const isOnline = store.isOnline();

        if (isOnline) updates.notifyRemoteVersion(remoteVersion, appVersion);
      });
    },
    onDestroy(store) {
      const client = store.centrifugoClient();
      const sub = store.centrifugoSubscription();
      sub?.unsubscribe();
      client?.disconnect();
    },
  }),
);
